<script lang="ts" setup>
  import { computed, onBeforeMount, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import { User } from '@stuut/api/models';
  import { ComboSelect } from '@ui/components/combo-select';
  import { useLoader } from '@ui/components/combo-select/useLoader';

  import { useUsers } from '@/store';

  interface Props {
    disabled?: boolean;
    queryName?: string;
  }

  const props = withDefaults(defineProps<Props>(), { queryName: 'assignee' });

  const emit = defineEmits<{
    (e: 'update', assignee: User | null): void;
  }>();

  const userStore = useUsers();

  const route = useRoute();
  const users = computed(() => userStore.users);
  const initiallyLoading = ref(false);

  const selectedAssignee = ref<User | null>(
    route.query?.[props.queryName] ? new User({ id: route.query?.[props.queryName] }) : null,
  );

  const assigneeLoader = useLoader({
    load: async (args?) => {
      if (!users.value.length) {
        await userStore.fetchUsers();
      }

      if (!args?.search) return users.value;

      const searchTerm = args.search.toLowerCase();

      return users.value.filter((user) => user.name?.toLowerCase()?.includes(searchTerm));
    },
  });

  onBeforeMount(async () => {
    const assigneeQPValue = route.query?.[props.queryName];

    if (assigneeQPValue) {
      const assigneeId = Array.isArray(assigneeQPValue) ? assigneeQPValue[0] : assigneeQPValue;

      if (assigneeId) {
        if (!users.value?.length) {
          initiallyLoading.value = true;
          await userStore.fetchUsers();
          initiallyLoading.value = false;
        }

        selectedAssignee.value = users.value.find((u) => u.id === assigneeId) ?? null;
      }
    }
  });

  defineExpose({
    selectedAssignee,
  });
</script>

<template>
  <ComboSelect
    v-model="selectedAssignee"
    :loader="assigneeLoader"
    :ignore-filter="true"
    searchable
    variant="filter"
    placeholder="Filter By Assignee"
    search-placeholder="Filter by assignee..."
    section-heading="Assignee"
    option-identifier="name"
    :additional-options="[User.fromJson({ id: '', name: 'No Assignee' })]"
    @update:model-value="(assignee) => emit('update', assignee as User | null)"
  >
    <template #filter-label>
      <i class="fas fa-user text-xs" />
      <span> Assignee </span>
    </template>

    <template #selected="{ value }">
      <i v-if="initiallyLoading" class="fa fa-spinner-third animate-spin" />

      <template v-else>
        {{ value instanceof User ? value.name : '' }}
      </template>
    </template>
  </ComboSelect>
</template>
